import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';
import GoldenScissors from '@/components/GoldenScissors';
import api from '@/services/api';
import { toast } from 'sonner';

// Webmail deep-link by email domain — one tap from "check your email" to the inbox.
function webmailFor(email) {
  const domain = (email.split('@')[1] || '').toLowerCase();
  if (domain === 'gmail.com' || domain === 'googlemail.com') return { name: 'Gmail', url: 'https://mail.google.com/mail/u/0/' };
  if (['outlook.com', 'hotmail.com', 'live.com', 'msn.com'].includes(domain)) return { name: 'Outlook', url: 'https://outlook.live.com/mail/' };
  if (['yahoo.com', 'yahoo.com.au', 'ymail.com'].includes(domain)) return { name: 'Yahoo Mail', url: 'https://mail.yahoo.com/' };
  if (['icloud.com', 'me.com', 'mac.com'].includes(domain)) return { name: 'iCloud Mail', url: 'https://www.icloud.com/mail/' };
  // Custom domains commonly sit on Gmail — a best-guess beats no button.
  return { name: 'Gmail', url: 'https://mail.google.com/mail/u/0/' };
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaReset, setCaptchaReset] = useState(0);
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Challenge runs NOW (not at page load) — token is always fresh at submit.
      const token = (await turnstileRef.current?.getToken()) || captchaToken;
      await api.post('/auth/forgot-password', { email, turnstile_token: token });
      setIsSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
      // Fresh single-use Turnstile token for the retry.
      setCaptchaReset((n) => n + 1);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-6 hero-gradient">
        <Card className="w-full max-w-md border border-border/40 shadow-lg">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center border border-primary/30">
              <GoldenScissors size="md" />
            </div>
            <h2 className="font-cormorant text-2xl text-primary">Check Your Email</h2>
            <p className="text-muted-foreground text-sm">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
              Please check your inbox and spam folder.
            </p>
            <p className="text-muted-foreground text-xs">
              The link will expire in 15 minutes.
            </p>
            <div className="pt-4 space-y-3">
              <Button
                className="btn-primary w-full"
                onClick={() => window.open(webmailFor(email).url, '_blank', 'noopener')}
              >
                <Mail className="mr-2 h-4 w-4" />
                Open {webmailFor(email).name}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setIsSubmitted(false); setEmail(''); }}
              >
                Try a different email
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6 hero-gradient">
      <Card className="w-full max-w-md border border-border/40 shadow-lg">
        <CardHeader className="space-y-2">
          <CardTitle className="font-cormorant text-3xl text-center text-primary">Forgot Password</CardTitle>
          <CardDescription className="text-center">
            Enter your email and we'll send you a link to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-card border-border"
              />
            </div>

            <TurnstileWidget ref={turnstileRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} resetSignal={captchaReset} />

            <Button type="submit" className="w-full btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Reset Link
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center">
              <ArrowLeft className="mr-1 h-3 w-3" />
              Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
